import React, { useEffect, useMemo, useState } from 'react'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import ApartmentIcon from '@mui/icons-material/Apartment'
import CloseIcon from '@mui/icons-material/Close'
import DashboardIcon from '@mui/icons-material/Dashboard'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import DnsIcon from '@mui/icons-material/Dns'
import LightModeIcon from '@mui/icons-material/LightMode'
import LogoutIcon from '@mui/icons-material/Logout'
import { get, limitToLast, query, ref } from 'firebase/database'
import { Link, Navigate, useNavigate } from 'react-router'
import cnergiLogo from '../resources/CNERGI_PNG@4x.png'
import { clearLoginSession, deleteBoard, deleteUser, getAllBoards, getAllUsers, getBoardInfo, getLoginSession, getUserProfile, updateUser } from '../services/authApi'
import { db } from '../services/firebase'
import './SuperAdminPage.css'

const DEVICE_DASHBOARD_LOG_LIMIT = 24

const adminSections = [
  { id: 'overview', label: 'Overview', icon: DashboardIcon },
  { id: 'accounts', label: 'Accounts', icon: AccountCircleIcon },
  { id: 'companies', label: 'Companies', icon: ApartmentIcon },
  { id: 'devices', label: 'Devices', icon: DnsIcon },
]

const accountRoles = [
  { value: 'all', label: 'All Users' },
  { value: 'user', label: 'User' },
  { value: 'tech', label: 'Tech' },
  { value: 'super tech', label: 'Super Tech' },
  { value: 'admin', label: 'Admin' },
  { value: 'super admin', label: 'Super Admin' },
]

function formatAccountName(user) {
  const nameParts = [user.firstName, user.middleName, user.lastName]
    .map((namePart) => String(namePart || '').trim())
    .filter(Boolean)

  return nameParts.join(' ') || user.name || user.fullName || 'Unnamed User'
}

function formatDeviceIds(deviceIds) {
  return parseDeviceIds(deviceIds).join(', ')
}

function parseDeviceIds(deviceIds) {
  const deviceIdList = Array.isArray(deviceIds) ? deviceIds : String(deviceIds || '').split(',')

  return deviceIdList
    .map((deviceId) => String(deviceId).trim())
    .filter(Boolean)
}

function getDeviceIdInputs(deviceIds) {
  const parsedDeviceIds = parseDeviceIds(deviceIds)

  return parsedDeviceIds.length ? parsedDeviceIds : ['']
}

function normalizeAccountRow(user) {
  const userId = user.userId || user.id || user.email || 'Unknown'
  const role = String(user.role || 'user').trim().toLowerCase()
  const deviceIds = formatDeviceIds(user.deviceIds)

  return {
    id: userId,
    name: formatAccountName(user),
    email: user.email || 'N/A',
    role,
    devices: deviceIds || 'None',
  }
}

function getProfileUser(profileResponse) {
  return profileResponse?.user
    || profileResponse?.data?.user
    || profileResponse?.data
    || profileResponse
    || {}
}

function getEditableAccountForm(user = {}) {
  const deviceIds = getDeviceIdInputs(user.deviceIds || user.devices)

  return {
    userId: user.userId || user.id || '',
    firstName: user.firstName || '',
    middleName: user.middleName || '',
    lastName: user.lastName || '',
    email: user.email || '',
    contactNo: user.contactNo || '',
    role: String(user.role || 'user').trim().toLowerCase(),
    deviceIds,
  }
}

function getSessionRole(session) {
  return String(session?.user?.role || session?.role || '').trim().toLowerCase()
}

function normalizeBoardRow(board) {
  if (typeof board === 'string') {
    return {
      id: board,
      deviceId: board,
      companyId: 'N/A',
    }
  }

  const deviceId = board.deviceId || board.deviceid || board.id || board.boardId || 'Unknown'
  const companyId = board.companyId || board.companyid || board.companyID || board.assignment || 'N/A'

  return {
    id: deviceId,
    deviceId,
    companyId,
  }
}

function normalizeCompanyRows(boards) {
  const companiesById = boards.reduce((companies, board) => {
    const normalizedBoard = normalizeBoardRow(board)
    const { companyId } = normalizedBoard
    const normalizedCompanyId = String(companyId || '').trim()

    if (!normalizedCompanyId || normalizedCompanyId === 'N/A') {
      return companies
    }

    const company = companies.get(normalizedCompanyId) || {
      id: normalizedCompanyId,
      companyId: normalizedCompanyId,
      name: 'Company name pending',
      contact: 'Contact pending',
      email: 'Email pending',
      phone: 'Phone pending',
      address: 'Address pending',
      status: 'Profile pending',
      devices: [],
    }

    company.devices.push(normalizedBoard.deviceId)
    companies.set(normalizedCompanyId, company)

    return companies
  }, new Map())

  return Array.from(companiesById.values())
    .sort((companyA, companyB) => companyA.companyId.localeCompare(companyB.companyId))
    .map((company) => ({
      ...company,
      deviceCount: company.devices.length,
    }))
}

function getBoardInfoPayload(boardInfoResponse) {
  return boardInfoResponse?.data
    || boardInfoResponse?.board
    || boardInfoResponse?.info
    || boardInfoResponse
    || {}
}

function getDisplayValue(value) {
  if (value === null || value === undefined || value === '') {
    return 'N/A'
  }

  if (typeof value === 'object') {
    return JSON.stringify(value)
  }

  return String(value)
}

function formatInfoLabel(label) {
  const normalizedLabel = String(label)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')

  return normalizedLabel
    .split(' ')
    .map((word) => {
      if (!word || word === word.toUpperCase()) {
        return word
      }

      return `${word.charAt(0).toUpperCase()}${word.slice(1)}`
    })
    .join(' ')
}

function flattenInfoFields(value, prefix = '') {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return [[prefix || 'Value', value]]
  }

  return Object.entries(value).flatMap(([key, nestedValue]) => {
    const nextPrefix = prefix ? `${prefix} ${formatInfoLabel(key)}` : formatInfoLabel(key)

    if (nestedValue && typeof nestedValue === 'object' && !Array.isArray(nestedValue)) {
      return flattenInfoFields(nestedValue, nextPrefix)
    }

    return [[nextPrefix, nestedValue]]
  })
}

function getBoardInfoEntries(deviceInfo) {
  if (!deviceInfo || typeof deviceInfo !== 'object') {
    return []
  }

  return Object.entries(deviceInfo).flatMap(([key, value]) => {
    const normalizedKey = key.toLowerCase()

    if (normalizedKey === 'control' || normalizedKey === 'response') {
      return []
    }

    if (normalizedKey === 'config' && value && typeof value === 'object') {
      const configEntries = []
      const deviceConfig = value.Device || value.device
      const rs485Config = value.RS485 || value.rs485

      if (deviceConfig) {
        configEntries.push(['Device', flattenInfoFields(deviceConfig)])
      }

      if (rs485Config) {
        configEntries.push(['RS485', flattenInfoFields(rs485Config)])
      }

      return configEntries
    }

    return [[formatInfoLabel(key), flattenInfoFields(value)]]
  })
}

function toDashboardNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string' && value.trim()) {
    const parsedValue = Number(value)
    return Number.isFinite(parsedValue) ? parsedValue : null
  }

  return null
}

function getDashboardLogPayload(log = {}) {
  return log?.data && typeof log.data === 'object' ? log.data : log
}

function getDashboardPower(log, direction) {
  const payload = getDashboardLogPayload(log)

  if (direction === 'import') {
    return toDashboardNumber(payload?.powerImport ?? payload?.gridImport ?? payload?.import ?? payload?.importPower ?? log?.devicedata?.meter?.import?.W)
  }

  return toDashboardNumber(payload?.powerExport ?? payload?.gridExport ?? payload?.export ?? payload?.exportPower ?? log?.devicedata?.meter?.export?.W)
}

function getDashboardStatus(log) {
  const payload = getDashboardLogPayload(log)
  const statusCode = payload?.ml?.status ?? payload?.status ?? payload?.statusCode ?? log?.ml?.status

  if (String(statusCode) === '1') return { label: 'Normal', tone: 'normal' }
  if (String(statusCode) === '2') return { label: 'Warning', tone: 'warning' }
  if (String(statusCode) === '3') return { label: 'Grid', tone: 'grid' }
  return { label: statusCode === undefined || statusCode === null || statusCode === '' ? 'Unavailable' : `Status ${statusCode}`, tone: 'neutral' }
}

function getDashboardFault(log) {
  const payload = getDashboardLogPayload(log)
  const fault = payload?.ml?.fault ?? payload?.fault ?? payload?.faultStatus ?? log?.ml?.fault

  return fault === undefined || fault === null || fault === '' ? 'N/A' : String(fault)
}

function normalizeDashboardLogs(rawLogs) {
  const logs = Array.isArray(rawLogs)
    ? rawLogs.map((value, index) => ({ _key: String(index), ...(value || {}) }))
    : Object.entries(rawLogs || {}).map(([key, value]) => ({ _key: key, ...(value || {}) }))

  return logs
    .filter((log) => log && typeof log === 'object')
    .map((log) => ({ ...log, timestamp: log.timestamp ?? log.ts ?? log.time ?? log._key }))
    .sort((logA, logB) => Number(logB.timestamp) - Number(logA.timestamp))
}

function formatDashboardPower(value) {
  return value === null ? 'N/A' : `${value.toFixed(2)} kW`
}

function formatDashboardTimestamp(value) {
  const numericValue = Number(value)
  const normalizedValue = Number.isFinite(numericValue) && numericValue < 100000000000 ? numericValue * 1000 : numericValue
  const date = new Date(normalizedValue)

  return Number.isNaN(date.getTime()) ? 'Latest reading' : `Updated ${date.toLocaleString()}`
}

function DeviceMiniDashboard({ logs, isLoading, error }) {
  if (isLoading) {
    return <p className="super-admin-status" role="status">Loading device dashboard...</p>
  }

  if (error) {
    return <p className="super-admin-status super-admin-status--error" role="alert">{error}</p>
  }

  if (!logs.length) {
    return (
      <div className="super-admin-mini-dashboard__empty">
        <strong>No readings available</strong>
        <span>This device does not have dashboard data yet.</span>
      </div>
    )
  }

  const latestLog = logs[0]
  const status = getDashboardStatus(latestLog)
  const chartLogs = logs.slice().reverse()
  const chartValues = chartLogs.flatMap((log) => [getDashboardPower(log, 'import'), getDashboardPower(log, 'export')]).filter((value) => value !== null)
  const maxChartValue = Math.max(...chartValues, 1)

  return (
    <div className="super-admin-mini-dashboard">
      <div className="super-admin-mini-dashboard__summary">
        <div>
          <span className={`super-admin-mini-dashboard__status super-admin-mini-dashboard__status--${status.tone}`}>
            <i aria-hidden="true"></i>{status.label}
          </span>
          <small>{formatDashboardTimestamp(latestLog.timestamp)}</small>
        </div>
        <span>{logs.length} recent readings</span>
      </div>

      <div className="super-admin-mini-dashboard__metrics" aria-label="Latest device metrics">
        <article>
          <span>Grid Import</span>
          <strong>{formatDashboardPower(getDashboardPower(latestLog, 'import'))}</strong>
          <i className="super-admin-mini-dashboard__metric-mark super-admin-mini-dashboard__metric-mark--import" aria-hidden="true"></i>
        </article>
        <article>
          <span>Grid Export</span>
          <strong>{formatDashboardPower(getDashboardPower(latestLog, 'export'))}</strong>
          <i className="super-admin-mini-dashboard__metric-mark super-admin-mini-dashboard__metric-mark--export" aria-hidden="true"></i>
        </article>
        <article>
          <span>Device Status</span>
          <strong>{status.label}</strong>
        </article>
        <article>
          <span>Fault</span>
          <strong>{getDashboardFault(latestLog)}</strong>
        </article>
      </div>

      <section className="super-admin-mini-dashboard__activity" aria-labelledby="device-activity-title">
        <div className="super-admin-mini-dashboard__activity-heading">
          <div>
            <h3 id="device-activity-title">Recent Grid Activity</h3>
            <span>Latest {chartLogs.length} samples</span>
          </div>
          <div className="super-admin-mini-dashboard__legend" aria-label="Chart legend">
            <span><i className="super-admin-mini-dashboard__legend-import"></i>Import</span>
            <span><i className="super-admin-mini-dashboard__legend-export"></i>Export</span>
          </div>
        </div>
        <div className="super-admin-mini-dashboard__chart" role="img" aria-label="Grid import and export activity chart">
          {chartLogs.map((log) => {
            const importValue = getDashboardPower(log, 'import')
            const exportValue = getDashboardPower(log, 'export')

            return (
              <div className="super-admin-mini-dashboard__chart-sample" key={log._key}>
                <i className="super-admin-mini-dashboard__bar super-admin-mini-dashboard__bar--import" style={{ height: `${((importValue || 0) / maxChartValue) * 100}%` }}></i>
                <i className="super-admin-mini-dashboard__bar super-admin-mini-dashboard__bar--export" style={{ height: `${((exportValue || 0) / maxChartValue) * 100}%` }}></i>
              </div>
            )
          })}
        </div>
      </section>
    </div>
  )
}

function SuperAdminOverview({ onSelectSection }) {
  const [summary, setSummary] = useState({
    accounts: 0,
    devices: 0,
    companies: 0,
  })
  const [isLoadingSummary, setIsLoadingSummary] = useState(true)
  const [summaryError, setSummaryError] = useState('')

  useEffect(() => {
    let isMounted = true

    async function loadSummary() {
      try {
        setIsLoadingSummary(true)
        setSummaryError('')
        const [users, boards] = await Promise.all([
          getAllUsers(),
          getAllBoards(),
        ])

        if (isMounted) {
          setSummary({
            accounts: users.length,
            devices: boards.length,
            companies: normalizeCompanyRows(boards).length,
          })
        }
      } catch (error) {
        if (isMounted) {
          setSummaryError(error.message || 'Unable to load overview.')
        }
      } finally {
        if (isMounted) {
          setIsLoadingSummary(false)
        }
      }
    }

    loadSummary()

    return () => {
      isMounted = false
    }
  }, [])

  const overviewItems = [
    {
      id: 'accounts',
      label: 'Accounts',
      value: summary.accounts,
      detail: 'Manage users and access roles',
      icon: AccountCircleIcon,
    },
    {
      id: 'companies',
      label: 'Companies',
      value: summary.companies,
      detail: 'View registered organizations',
      icon: ApartmentIcon,
    },
    {
      id: 'devices',
      label: 'Devices',
      value: summary.devices,
      detail: 'Review board inventory',
      icon: DnsIcon,
    },
  ]

  return (
    <div className="super-admin-overview">
      {summaryError ? (
        <p className="super-admin-overview__message" role="alert">
          {summaryError}
        </p>
      ) : null}

      <section className="super-admin-kpis" aria-label="Super Admin summary" aria-busy={isLoadingSummary}>
        {overviewItems.map(({ id, label, value, detail, icon: Icon }) => (
          <button
            className="super-admin-kpi"
            key={id}
            type="button"
            onClick={() => onSelectSection(id)}
          >
            <Icon aria-hidden="true" />
            <span>{label}</span>
            <strong>{isLoadingSummary ? '...' : value}</strong>
            <p>{detail}</p>
          </button>
        ))}
      </section>
    </div>
  )
}

function SuperAdminAccounts() {
  const [selectedRole, setSelectedRole] = useState(accountRoles[0].value)
  const [accounts, setAccounts] = useState([])
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true)
  const [accountsError, setAccountsError] = useState('')
  const [deletingUserId, setDeletingUserId] = useState('')
  const [editingAccount, setEditingAccount] = useState(null)
  const [editForm, setEditForm] = useState(getEditableAccountForm())
  const [isLoadingEditAccount, setIsLoadingEditAccount] = useState(false)
  const [isSavingEditAccount, setIsSavingEditAccount] = useState(false)
  const [editAccountError, setEditAccountError] = useState('')
  const selectedRoleLabel = accountRoles.find((role) => role.value === selectedRole)?.label || 'User'
  const visibleAccounts = selectedRole === 'all'
    ? accounts
    : accounts.filter((account) => account.role === selectedRole)

  useEffect(() => {
    let isMounted = true

    async function loadAccounts() {
      try {
        setIsLoadingAccounts(true)
        setAccountsError('')
        const users = await getAllUsers()

        if (isMounted) {
          setAccounts(users.map(normalizeAccountRow))
        }
      } catch (error) {
        if (isMounted) {
          setAccountsError(error.message || 'Unable to fetch users.')
          setAccounts([])
        }
      } finally {
        if (isMounted) {
          setIsLoadingAccounts(false)
        }
      }
    }

    loadAccounts()

    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    if (!editingAccount) {
      return undefined
    }

    let isMounted = true

    async function loadEditableAccount() {
      try {
        setIsLoadingEditAccount(true)
        setEditAccountError('')
        setEditForm(getEditableAccountForm(editingAccount))

        if (!editingAccount.email || editingAccount.email === 'N/A') {
          throw new Error('This account does not have an email address to load.')
        }

        const profileResponse = await getUserProfile(editingAccount.email)
        const profileUser = getProfileUser(profileResponse)

        if (isMounted) {
          setEditForm(getEditableAccountForm(profileUser))
        }
      } catch (error) {
        if (isMounted) {
          setEditAccountError(error.message || 'Unable to load account details.')
        }
      } finally {
        if (isMounted) {
          setIsLoadingEditAccount(false)
        }
      }
    }

    loadEditableAccount()

    return () => {
      isMounted = false
    }
  }, [editingAccount])

  async function handleDeleteAccount(account) {
    const shouldDelete = window.confirm(`Delete ${account.name}? This cannot be undone.`)

    if (!shouldDelete) {
      return
    }

    try {
      setDeletingUserId(account.id)
      setAccountsError('')
      await deleteUser(account.id)
      setAccounts((currentAccounts) => currentAccounts.filter((currentAccount) => currentAccount.id !== account.id))
    } catch (error) {
      setAccountsError(error.message || 'Unable to delete user.')
    } finally {
      setDeletingUserId('')
    }
  }

  function closeEditModal() {
    setEditingAccount(null)
    setEditAccountError('')
    setEditForm(getEditableAccountForm())
  }

  function handleEditFieldChange(event) {
    const { name, value } = event.target
    setEditForm((currentForm) => ({
      ...currentForm,
      [name]: value,
    }))
  }

  function handleDeviceIdChange(index, value) {
    setEditForm((currentForm) => {
      const nextDeviceIds = [...currentForm.deviceIds]
      nextDeviceIds[index] = value

      return {
        ...currentForm,
        deviceIds: nextDeviceIds,
      }
    })
  }

  function handleDeviceIdBlur(index, value) {
    const parsedDeviceIds = parseDeviceIds(value)

    if (parsedDeviceIds.length <= 1) {
      handleDeviceIdChange(index, parsedDeviceIds[0] || '')
      return
    }

    setEditForm((currentForm) => {
      const nextDeviceIds = [...currentForm.deviceIds]
      nextDeviceIds.splice(index, 1, ...parsedDeviceIds)

      return {
        ...currentForm,
        deviceIds: nextDeviceIds,
      }
    })
  }

  function handleAddDeviceId() {
    setEditForm((currentForm) => ({
      ...currentForm,
      deviceIds: [...currentForm.deviceIds, ''],
    }))
  }

  function handleRemoveDeviceId(index) {
    setEditForm((currentForm) => {
      const nextDeviceIds = currentForm.deviceIds.filter((_, deviceIdIndex) => deviceIdIndex !== index)

      return {
        ...currentForm,
        deviceIds: nextDeviceIds.length ? nextDeviceIds : [''],
      }
    })
  }

  async function handleSaveEditAccount() {
    const userId = editForm.userId || editingAccount?.id
    const deviceIds = formatDeviceIds(editForm.deviceIds)

    try {
      setIsSavingEditAccount(true)
      setEditAccountError('')
      await updateUser(userId, {
        firstName: editForm.firstName.trim(),
        middleName: editForm.middleName.trim(),
        lastName: editForm.lastName.trim(),
        email: editForm.email.trim(),
        contactNo: editForm.contactNo.trim(),
        role: editForm.role,
        deviceIds,
      })

      const updatedAccount = normalizeAccountRow({
        userId,
        firstName: editForm.firstName,
        middleName: editForm.middleName,
        lastName: editForm.lastName,
        email: editForm.email,
        role: editForm.role,
        deviceIds,
      })

      setAccounts((currentAccounts) => currentAccounts.map((account) => (
        account.id === editingAccount.id ? updatedAccount : account
      )))
      closeEditModal()
    } catch (error) {
      setEditAccountError(error.message || 'Unable to update user.')
    } finally {
      setIsSavingEditAccount(false)
    }
  }

  return (
    <>
      <section className="super-admin-panel super-admin-accounts" aria-labelledby="accounts-title">
        <div className="super-admin-panel__heading">
          <div>
            <h2 id="accounts-title">Accounts</h2>
            <span>Filter accounts by access role</span>
          </div>
          <label className="super-admin-role-select">
            <span>Role</span>
            <select value={selectedRole} onChange={(event) => setSelectedRole(event.target.value)}>
              {accountRoles.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>
        </div>

        {isLoadingAccounts ? (
          <p className="super-admin-status" role="status">Loading users...</p>
        ) : null}

        {accountsError ? (
          <p className="super-admin-status super-admin-status--error" role="alert">{accountsError}</p>
        ) : null}

        <div className="super-admin-table-wrap">
          <table className="super-admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Devices</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {visibleAccounts.map(({ id, name, email, role, devices }) => (
                <tr key={id}>
                  <td>{id}</td>
                  <td>{name}</td>
                  <td>{email}</td>
                  <td>
                    {selectedRole === 'all'
                      ? accountRoles.find((accountRole) => accountRole.value === role)?.label || role
                      : selectedRoleLabel}
                  </td>
                  <td>{devices}</td>
                  <td>
                    <div className="super-admin-table__actions">
                      <button
                        className="super-admin-table__edit"
                        type="button"
                        onClick={() => setEditingAccount({ id, name, email, role, devices })}
                      >
                        Edit
                      </button>
                      <button
                        className="super-admin-table__delete"
                        type="button"
                        disabled={deletingUserId === id}
                        onClick={() => handleDeleteAccount({ id, name })}
                      >
                        {deletingUserId === id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!isLoadingAccounts && !accountsError && !visibleAccounts.length ? (
                <tr>
                  <td colSpan="6">No users found.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      {editingAccount ? (
        <div className="super-admin-modal" role="presentation" onMouseDown={closeEditModal}>
          <section
            className="super-admin-modal__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="super-admin-edit-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="super-admin-modal__header">
              <div>
                <h2 id="super-admin-edit-title">Edit Account</h2>
                <span>{editingAccount.name}</span>
              </div>
              <button className="super-admin-modal__close" type="button" aria-label="Close edit account modal" onClick={closeEditModal}>
                <CloseIcon aria-hidden="true" />
              </button>
            </header>

            <div className="super-admin-modal__body">
              {isLoadingEditAccount ? (
                <p className="super-admin-status" role="status">Loading account details...</p>
              ) : null}

              {editAccountError ? (
                <p className="super-admin-status super-admin-status--error" role="alert">{editAccountError}</p>
              ) : null}

              <form className="super-admin-edit-form" onSubmit={(event) => event.preventDefault()}>
                <label>
                  <span>User ID</span>
                  <input name="userId" value={editForm.userId} onChange={handleEditFieldChange} />
                </label>
                <label>
                  <span>First Name</span>
                  <input name="firstName" value={editForm.firstName} onChange={handleEditFieldChange} />
                </label>
                <label>
                  <span>Middle Name</span>
                  <input name="middleName" value={editForm.middleName} onChange={handleEditFieldChange} />
                </label>
                <label>
                  <span>Last Name</span>
                  <input name="lastName" value={editForm.lastName} onChange={handleEditFieldChange} />
                </label>
                <label>
                  <span>Email</span>
                  <input name="email" type="email" value={editForm.email} onChange={handleEditFieldChange} />
                </label>
                <label>
                  <span>Contact Number</span>
                  <input name="contactNo" value={editForm.contactNo} onChange={handleEditFieldChange} />
                </label>
                <label>
                  <span>Role</span>
                  <select name="role" value={editForm.role} onChange={handleEditFieldChange}>
                    {accountRoles.filter((role) => role.value !== 'all').map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </label>
                <fieldset className="super-admin-device-ids">
                  <legend>Device IDs</legend>
                  <div className="super-admin-device-ids__list">
                    {editForm.deviceIds.map((deviceId, index) => (
                      <div className="super-admin-device-ids__row" key={`device-id-${index}`}>
                        <input
                          aria-label={`Device ID ${index + 1}`}
                          value={deviceId}
                          onChange={(event) => handleDeviceIdChange(index, event.target.value)}
                          onBlur={(event) => handleDeviceIdBlur(index, event.target.value)}
                        />
                        <button type="button" onClick={() => handleRemoveDeviceId(index)}>
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                  <button className="super-admin-device-ids__add" type="button" onClick={handleAddDeviceId}>
                    Add Device ID
                  </button>
                </fieldset>
              </form>
            </div>

            <footer className="super-admin-modal__footer">
              <button className="super-admin-modal__cancel" type="button" onClick={closeEditModal}>
                Cancel
              </button>
              <button
                className="super-admin-modal__save"
                type="button"
                disabled={isLoadingEditAccount || isSavingEditAccount}
                onClick={handleSaveEditAccount}
              >
                {isSavingEditAccount ? 'Saving...' : 'Save Changes'}
              </button>
            </footer>
          </section>
        </div>
      ) : null}
    </>
  )
}

function SuperAdminCompanies() {
  const [companies, setCompanies] = useState([])
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true)
  const [companiesError, setCompaniesError] = useState('')
  const [selectedCompany, setSelectedCompany] = useState(null)

  useEffect(() => {
    let isMounted = true

    async function loadCompanies() {
      try {
        setIsLoadingCompanies(true)
        setCompaniesError('')
        const boards = await getAllBoards()

        if (isMounted) {
          setCompanies(normalizeCompanyRows(boards))
        }
      } catch (error) {
        if (isMounted) {
          setCompaniesError(error.message || 'Unable to fetch companies.')
          setCompanies([])
        }
      } finally {
        if (isMounted) {
          setIsLoadingCompanies(false)
        }
      }
    }

    loadCompanies()

    return () => {
      isMounted = false
    }
  }, [])

  return (
    <section className="super-admin-panel super-admin-list" aria-labelledby="companies-title">
      <div className="super-admin-panel__heading">
        <div>
          <h2 id="companies-title">Companies</h2>
          <span>Customer organizations from registered devices</span>
        </div>
      </div>

      {isLoadingCompanies ? (
        <p className="super-admin-status" role="status">Loading companies...</p>
      ) : null}

      {companiesError ? (
        <p className="super-admin-status super-admin-status--error" role="alert">{companiesError}</p>
      ) : null}

      <div className="super-admin-table-wrap">
        <table className="super-admin-table">
          <thead>
            <tr>
              <th>Company ID</th>
              <th>Devices</th>
            </tr>
          </thead>
          <tbody>
            {companies.map((company) => (
              <tr key={company.id}>
                <td>
                  <button
                    className="super-admin-table__link"
                    type="button"
                    onClick={() => setSelectedCompany(company)}
                  >
                    {company.companyId}
                  </button>
                </td>
                <td>{company.deviceCount}</td>
              </tr>
            ))}
            {!isLoadingCompanies && !companiesError && !companies.length ? (
              <tr>
                <td colSpan="2">No companies found.</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {selectedCompany ? (
        <div className="super-admin-modal" role="presentation" onMouseDown={() => setSelectedCompany(null)}>
          <section
            className="super-admin-modal__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="super-admin-company-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="super-admin-modal__header">
              <div>
                <h2 id="super-admin-company-title">Company Profile</h2>
                <span>{selectedCompany.companyId}</span>
              </div>
              <button className="super-admin-modal__close" type="button" aria-label="Close company devices modal" onClick={() => setSelectedCompany(null)}>
                <CloseIcon aria-hidden="true" />
              </button>
            </header>

            <div className="super-admin-modal__body">
              <section className="super-admin-company-profile" aria-label="Company profile">
                <label>
                  <span>Company Name</span>
                  <input value={selectedCompany.name} readOnly />
                </label>
                <label>
                  <span>Contact Person</span>
                  <input value={selectedCompany.contact} readOnly />
                </label>
                <label>
                  <span>Email</span>
                  <input value={selectedCompany.email} readOnly />
                </label>
                <label>
                  <span>Phone</span>
                  <input value={selectedCompany.phone} readOnly />
                </label>
                <label>
                  <span>Address</span>
                  <input value={selectedCompany.address} readOnly />
                </label>
                <label>
                  <span>Status</span>
                  <input value={selectedCompany.status} readOnly />
                </label>
              </section>

              <section className="super-admin-company-devices" aria-label="Company devices">
                <h3>Devices</h3>
                <div className="super-admin-table-wrap">
                  <table className="super-admin-table">
                    <thead>
                      <tr>
                        <th>Device ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedCompany.devices.map((deviceId, deviceIndex) => (
                        <tr key={`${deviceId}-${deviceIndex}`}>
                          <td>{deviceId}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>

            <footer className="super-admin-modal__footer">
              <button className="super-admin-modal__cancel" type="button" onClick={() => setSelectedCompany(null)}>
                Close
              </button>
            </footer>
          </section>
        </div>
      ) : null}
    </section>
  )
}

function SuperAdminDevices() {
  const [devices, setDevices] = useState([])
  const [isLoadingDevices, setIsLoadingDevices] = useState(true)
  const [devicesError, setDevicesError] = useState('')
  const [deletingDeviceId, setDeletingDeviceId] = useState('')
  const [viewingDeviceId, setViewingDeviceId] = useState('')
  const [deviceInfo, setDeviceInfo] = useState(null)
  const [isLoadingDeviceInfo, setIsLoadingDeviceInfo] = useState(false)
  const [deviceInfoError, setDeviceInfoError] = useState('')
  const [activeDeviceTab, setActiveDeviceTab] = useState('info')
  const [deviceDashboardLogs, setDeviceDashboardLogs] = useState([])
  const [isLoadingDeviceDashboard, setIsLoadingDeviceDashboard] = useState(false)
  const [deviceDashboardError, setDeviceDashboardError] = useState('')

  useEffect(() => {
    let isMounted = true

    async function loadDevices() {
      try {
        setIsLoadingDevices(true)
        setDevicesError('')
        const boards = await getAllBoards()

        if (isMounted) {
          setDevices(boards.map(normalizeBoardRow))
        }
      } catch (error) {
        if (isMounted) {
          setDevicesError(error.message || 'Unable to fetch devices.')
          setDevices([])
        }
      } finally {
        if (isMounted) {
          setIsLoadingDevices(false)
        }
      }
    }

    loadDevices()

    return () => {
      isMounted = false
    }
  }, [])

  async function handleDeleteDevice(deviceId) {
    const shouldDelete = window.confirm(`Delete ${deviceId}? This cannot be undone.`)

    if (!shouldDelete) {
      return
    }

    try {
      setDeletingDeviceId(deviceId)
      setDevicesError('')
      await deleteBoard(deviceId)
      setDevices((currentDevices) => currentDevices.filter((device) => device.id !== deviceId))
    } catch (error) {
      setDevicesError(error.message || 'Unable to delete device.')
    } finally {
      setDeletingDeviceId('')
    }
  }

  async function handleViewDevice(deviceId) {
    setViewingDeviceId(deviceId)
    setActiveDeviceTab('info')
    setDeviceInfo(null)
    setDeviceInfoError('')
    setDeviceDashboardLogs([])
    setDeviceDashboardError('')
    setIsLoadingDeviceInfo(true)
    setIsLoadingDeviceDashboard(true)

    const logsRef = query(ref(db, `EMS/Logs/${deviceId}/data`), limitToLast(DEVICE_DASHBOARD_LOG_LIMIT))
    const [infoResult, dashboardResult] = await Promise.allSettled([
      getBoardInfo(deviceId),
      get(logsRef),
    ])

    if (infoResult.status === 'fulfilled') {
      setDeviceInfo(getBoardInfoPayload(infoResult.value))
    } else {
      setDeviceInfoError(infoResult.reason?.message || 'Unable to fetch device info.')
    }

    if (dashboardResult.status === 'fulfilled') {
      setDeviceDashboardLogs(dashboardResult.value.exists() ? normalizeDashboardLogs(dashboardResult.value.val()) : [])
    } else {
      setDeviceDashboardError(dashboardResult.reason?.message || 'Unable to fetch dashboard readings.')
    }

    setIsLoadingDeviceInfo(false)
    setIsLoadingDeviceDashboard(false)
  }

  function closeDeviceInfoModal() {
    setViewingDeviceId('')
    setDeviceInfo(null)
    setDeviceInfoError('')
    setActiveDeviceTab('info')
    setDeviceDashboardLogs([])
    setDeviceDashboardError('')
  }

  const deviceInfoEntries = getBoardInfoEntries(deviceInfo)

  return (
    <>
      <section className="super-admin-panel super-admin-list" aria-labelledby="devices-title">
        <div className="super-admin-panel__heading">
          <div>
            <h2 id="devices-title">Devices</h2>
            <span>Registered board inventory</span>
          </div>
        </div>

        {isLoadingDevices ? (
          <p className="super-admin-status" role="status">Loading devices...</p>
        ) : null}

        {devicesError ? (
          <p className="super-admin-status super-admin-status--error" role="alert">{devicesError}</p>
        ) : null}

        <div className="super-admin-table-wrap">
          <table className="super-admin-table">
            <thead>
              <tr>
                <th>Device ID</th>
                <th>Company ID</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {devices.map(({ id, deviceId, companyId }) => (
                <tr key={id}>
                  <td>{deviceId}</td>
                  <td>{companyId}</td>
                  <td>
                    <div className="super-admin-table__actions">
                      <button className="super-admin-table__edit" type="button" onClick={() => handleViewDevice(id)}>
                        View
                      </button>
                      <button
                        className="super-admin-table__delete"
                        type="button"
                        disabled={deletingDeviceId === id}
                        onClick={() => handleDeleteDevice(id)}
                      >
                        {deletingDeviceId === id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!isLoadingDevices && !devicesError && !devices.length ? (
                <tr>
                  <td colSpan="3">No devices found.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      {viewingDeviceId ? (
        <div className="super-admin-modal" role="presentation" onMouseDown={closeDeviceInfoModal}>
          <section
            className="super-admin-modal__dialog super-admin-modal__dialog--device"
            role="dialog"
            aria-modal="true"
            aria-labelledby="super-admin-device-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="super-admin-modal__header">
              <div>
                <h2 id="super-admin-device-title">Device Info</h2>
                <span>{viewingDeviceId}</span>
              </div>
              <button className="super-admin-modal__close" type="button" aria-label="Close device info modal" onClick={closeDeviceInfoModal}>
                <CloseIcon aria-hidden="true" />
              </button>
            </header>

            <div className="super-admin-device-tabs" role="tablist" aria-label="Device details">
              <button
                className={activeDeviceTab === 'info' ? 'super-admin-device-tabs__button super-admin-device-tabs__button--active' : 'super-admin-device-tabs__button'}
                type="button"
                role="tab"
                aria-selected={activeDeviceTab === 'info'}
                aria-controls="super-admin-device-info-panel"
                id="super-admin-device-info-tab"
                onClick={() => setActiveDeviceTab('info')}
              >
                Device Info
              </button>
              <button
                className={activeDeviceTab === 'dashboard' ? 'super-admin-device-tabs__button super-admin-device-tabs__button--active' : 'super-admin-device-tabs__button'}
                type="button"
                role="tab"
                aria-selected={activeDeviceTab === 'dashboard'}
                aria-controls="super-admin-device-dashboard-panel"
                id="super-admin-device-dashboard-tab"
                onClick={() => setActiveDeviceTab('dashboard')}
              >
                Device Dashboard
              </button>
            </div>

            <div
              className="super-admin-modal__body"
              role="tabpanel"
              id={activeDeviceTab === 'info' ? 'super-admin-device-info-panel' : 'super-admin-device-dashboard-panel'}
              aria-labelledby={activeDeviceTab === 'info' ? 'super-admin-device-info-tab' : 'super-admin-device-dashboard-tab'}
            >
              {activeDeviceTab === 'info' ? (
                <>
              {isLoadingDeviceInfo ? (
                <p className="super-admin-status" role="status">Loading device info...</p>
              ) : null}

              {deviceInfoError ? (
                <p className="super-admin-status super-admin-status--error" role="alert">{deviceInfoError}</p>
              ) : null}

              {!isLoadingDeviceInfo && !deviceInfoError && deviceInfoEntries.length ? (
                <div className="super-admin-info-list">
                  {deviceInfoEntries.map(([sectionLabel, fields]) => (
                    <section className="super-admin-info-section" key={sectionLabel}>
                      <h3>{sectionLabel}</h3>
                      <div className="super-admin-info-fields">
                        {fields.map(([fieldLabel, fieldValue]) => (
                          <label key={`${sectionLabel}-${fieldLabel}`}>
                            <span>{fieldLabel}</span>
                            <input value={getDisplayValue(fieldValue)} readOnly />
                          </label>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              ) : null}
                </>
              ) : (
                <DeviceMiniDashboard
                  logs={deviceDashboardLogs}
                  isLoading={isLoadingDeviceDashboard}
                  error={deviceDashboardError}
                />
              )}
            </div>

            <footer className="super-admin-modal__footer">
              <button className="super-admin-modal__cancel" type="button" onClick={closeDeviceInfoModal}>
                Close
              </button>
            </footer>
          </section>
        </div>
      ) : null}
    </>
  )
}

function SuperAdminPage({ isDarkMode = false, onToggleTheme }) {
  const navigate = useNavigate()
  const loginSession = getLoginSession()
  const sessionRole = getSessionRole(loginSession)
  const [activeSectionId, setActiveSectionId] = useState(adminSections[0].id)
  const activeSection = useMemo(
    () => adminSections.find((section) => section.id === activeSectionId) || adminSections[0],
    [activeSectionId]
  )
  const ActiveSectionIcon = activeSection.icon

  const handleLogout = () => {
    clearLoginSession()
    navigate('/login', { replace: true })
  }

  if (sessionRole !== 'super admin') {
    return <Navigate to="/login" replace />
  }

  return (
    <main className="super-admin-page" aria-label="Super Admin">
      <aside className="super-admin-sidebar" aria-label="Super Admin navigation">
        <Link className="super-admin-sidebar__brand" to="/" aria-label="CNERGI home">
          <img src={cnergiLogo} alt="CNERGI" />
        </Link>

        <div className="super-admin-sidebar__identity">
          <AdminPanelSettingsIcon aria-hidden="true" />
          <span>Super Admin</span>
        </div>

        <nav className="super-admin-sidebar__nav">
          {adminSections.map(({ id, label, icon: Icon }) => (
            <button
              className={`super-admin-sidebar__button ${activeSectionId === id ? 'super-admin-sidebar__button--active' : ''}`}
              key={id}
              type="button"
              aria-current={activeSectionId === id ? 'page' : undefined}
              onClick={() => setActiveSectionId(id)}
            >
              <Icon aria-hidden="true" />
              <span>{label}</span>
            </button>
          ))}
        </nav>

      </aside>

      <section className="super-admin-main" aria-labelledby="super-admin-title">
        <header className="super-admin-header">
          <div>
            <h1 id="super-admin-title">Super Admin</h1>
          </div>
          <div className="super-admin-header__actions">
            <p>{activeSection.label}</p>
            <button
              className="super-admin-theme-toggle"
              type="button"
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              onClick={onToggleTheme}
            >
              {isDarkMode ? <LightModeIcon aria-hidden="true" /> : <DarkModeIcon aria-hidden="true" />}
              <span>{isDarkMode ? 'Light' : 'Dark'}</span>
            </button>
            <button
              className="super-admin-logout"
              type="button"
              onClick={handleLogout}
            >
              <LogoutIcon aria-hidden="true" />
              <span>Logout</span>
            </button>
          </div>
        </header>

        <div className="super-admin-workspace">
          {activeSectionId === 'overview' ? (
            <SuperAdminOverview onSelectSection={setActiveSectionId} />
          ) : activeSectionId === 'accounts' ? (
            <SuperAdminAccounts />
          ) : activeSectionId === 'companies' ? (
            <SuperAdminCompanies />
          ) : activeSectionId === 'devices' ? (
            <SuperAdminDevices />
          ) : (
            <div className="super-admin-workspace__empty">
              <ActiveSectionIcon aria-hidden="true" />
              <h2>{activeSection.label}</h2>
            </div>
          )}
        </div>
      </section>
    </main>
  )
}

export default SuperAdminPage
